import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import {
  paymentMethodMeta,
  methodConfigFields,
} from './paymentMethodsData';
import {
  fetchPaymentGateways,
  savePaymentGateway,
  findGateway,
  getMergedConfig,
} from '../../../services/gym/paymentGatewayApi';
import { usePanelToast } from '../../../context/PanelToastContext';
import '../../owner/ownerPages.css';
import '../../owner/notifications/notifications.css';
import '../../owner/subscriptions/subscriptions.css';
import '../adminListPages.css';
import './paymentMethods.css';

function ToggleSetting({ checked, onChange, disabled }) {
  return (
    <label className="notification-toggle admin-payment-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span className="notification-toggle-slider" />
    </label>
  );
}

function validateApiCredentials(key, config) {
  if (key === 'stripe') {
    if (!config.stripePublishableKey?.trim() || !config.stripeSecretKey?.trim()) {
      return 'Stripe Publishable Key and Secret Key are required to connect via API.';
    }
  }
  if (key === 'square') {
    if (!config.squareApplicationId?.trim() || !config.squareAccessToken?.trim() || !config.squareLocationId?.trim()) {
      return 'Square Application ID, Access Token, and Location ID are required to connect via API.';
    }
  }
  return '';
}

function ConfigField({ field, methodKey, value, onChange }) {
  const id = `${methodKey}-${field.name}`;

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        name={field.name}
        className="owner-form-input owner-form-textarea"
        rows={field.rows || 4}
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e)}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        id={id}
        name={field.name}
        className="owner-form-select"
        value={value}
        onChange={(e) => onChange(e)}
      >
        {field.options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      id={id}
      name={field.name}
      type={field.type || 'text'}
      className="owner-form-input"
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e)}
    />
  );
}

function PaymentMethodsManagement() {
  const toast = usePanelToast();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configureKey, setConfigureKey] = useState(null);
  const [config, setConfig] = useState({});
  const [savedKey, setSavedKey] = useState(null);
  const [connectingKey, setConnectingKey] = useState(null);
  const [connectError, setConnectError] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    method: '',
    type: '',
    status: '',
    apiConnection: '',
  });

  const typeLabel = (method) => (method.connectType === 'api' ? 'API Gateway' : 'Manual');
  const statusLabel = (method) => (method.enabled ? 'Enabled' : 'Disabled');
  const apiConnectionLabel = (method) => (
    method.connectType === 'api'
      ? (method.apiConnected ? 'Connected' : 'Not connected')
      : '—'
  );

  const loadGateways = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setGateways(await fetchPaymentGateways());
    } catch (err) {
      setError(err.message || 'Failed to load payment gateways');
      setGateways([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  const methodsByKey = useMemo(() => {
    const map = new Map(gateways.map((g) => [g.methodKey, g]));
    return paymentMethodMeta.map((meta) => {
      const gateway = map.get(meta.key);
      return {
        ...meta,
        id: gateway?.id,
        enabled: gateway?.enabled ?? false,
        apiConnected: gateway?.apiConnected ?? false,
        gatewayConfig: gateway?.config || {},
      };
    });
  }, [gateways]);

  const filteredMethods = useMemo(() => (
    methodsByKey.filter((method) => (
      matchTextFilter(method.label, filters.method)
      && matchTextFilter(typeLabel(method), filters.type)
      && matchTextFilter(statusLabel(method), filters.status)
      && matchTextFilter(apiConnectionLabel(method), filters.apiConnection)
    ))
  ), [methodsByKey, filters]);

  const persistGateway = async (methodKey, updates) => {
    const current = findGateway(gateways, methodKey);
    if (!current) throw new Error('Payment gateway not found.');

    const updated = await savePaymentGateway({
      ...current,
      ...updates,
      config: updates.config ?? current.config,
    });

    setGateways((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    return updated;
  };

  const toggleMethod = async (key, value) => {
    try {
      setSaving(true);
      setError('');
      await persistGateway(key, { enabled: value });
      toast.showUpdated('Payment gateway');
    } catch (err) {
      setError(err.message || 'Failed to update payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    setSavedKey(null);
    setConnectError('');
    setTestMessage('');
  };

  const handleConfigure = (key) => {
    if (configureKey === key) {
      setConfigureKey(null);
      return;
    }

    const gateway = findGateway(gateways, key);
    setConfig(gateway?.config || getMergedConfig(gateways));
    setConfigureKey(key);
    setSavedKey(null);
    setConnectError('');
    setTestMessage('');
  };

  const handleConnectApi = async (key) => {
    const error = validateApiCredentials(key, config);
    if (error) {
      setConnectError(error);
      return;
    }

    try {
      setConnectingKey(key);
      setConnectError('');
      setTestMessage('');
      await persistGateway(key, {
        enabled: true,
        apiConnected: true,
        config,
      });
      toast.showUpdated('Payment gateway');
    } catch (err) {
      setConnectError(err.message || 'Failed to connect payment gateway');
    } finally {
      setConnectingKey(null);
    }
  };

  const handleDisconnectApi = async (key) => {
    try {
      setSaving(true);
      await persistGateway(key, { enabled: false, apiConnected: false });
      toast.showUpdated('Payment gateway');
      setConnectError('');
      setTestMessage('');
    } catch (err) {
      setError(err.message || 'Failed to disconnect payment gateway');
    } finally {
      setSaving(false);
    }
  };

  const handleTestApi = (key) => {
    const validationError = validateApiCredentials(key, config);
    if (validationError) {
      setConnectError(validationError);
      return;
    }

    setConnectingKey(key);
    setConnectError('');
    setTestMessage('');

    window.setTimeout(() => {
      setConnectingKey(null);
      setTestMessage(`${key === 'stripe' ? 'Stripe' : 'Square'} API connection successful.`);
    }, 600);
  };

  const handleSaveMethod = async (key) => {
    try {
      setSaving(true);
      setError('');
      await persistGateway(key, { config });
      toast.showSaved('Payment gateway settings');
    } catch (err) {
      setError(err.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-payment-page">
      <PageHeader title="Payment Gateway" subtitle="Configure accepted payment methods and gateway settings" />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading payment gateways...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card owner-mb-28">
        <h2 className="owner-card-title">Payment Gateway Overview</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Method"
                  filterKey="method"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search method..."
                />
                <ColumnFilterHeader
                  label="Type"
                  filterKey="type"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search type..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <ColumnFilterHeader
                  label="API Connection"
                  filterKey="apiConnection"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search connection..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMethods.length > 0 ? filteredMethods.map((method) => (
                <tr key={method.key}>
                  <td>
                    <div className="admin-payment-table-method">
                      <span className="payment-method-icon">{method.icon}</span>
                      <span>{method.label}</span>
                    </div>
                  </td>
                  <td>{method.connectType === 'api' ? 'API Gateway' : 'Manual'}</td>
                  <td>
                    <span className={`owner-badge ${method.enabled ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                      {method.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    {method.connectType === 'api' ? (
                      <span className={`owner-badge ${method.apiConnected ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                        {method.apiConnected ? 'Connected' : 'Not connected'}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="owner-btn owner-btn-secondary owner-btn-sm"
                      onClick={() => handleConfigure(method.key)}
                      disabled={loading || saving}
                    >
                      {method.connectType === 'api' ? 'Configure API' : 'Configure'}
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No payment gateways configured'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-payment-section">
        <h2 className="owner-card-title">Payment Methods</h2>
        <div className="admin-payment-methods-list">
          {methodsByKey.map((method) => {
            const isOpen = configureKey === method.key;
            const fields = methodConfigFields[method.key] || [];
            const isApiMethod = method.connectType === 'api';
            const isConnected = method.apiConnected;

            return (
              <div key={method.key} className="admin-payment-method-item">
                {isOpen && (
                  <div className="admin-payment-config-panel">
                    <div className="admin-payment-config-panel-header">
                      <h3 className="admin-payment-config-title">
                        {isApiMethod ? `Connect ${method.label} via API` : `Configure ${method.label}`}
                      </h3>
                      <button
                        type="button"
                        className="subscription-modal-close"
                        onClick={() => setConfigureKey(null)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {isApiMethod && (
                      <div className="admin-payment-api-connect">
                        <p className="admin-payment-api-hint">
                          Enter your {method.label} API credentials from the developer dashboard, then connect to enable payments.
                        </p>
                        <div className="admin-payment-api-status">
                          {isConnected ? (
                            <span className="owner-badge owner-badge-success">Connected via API</span>
                          ) : (
                            <span className="owner-badge owner-badge-warning">Not connected</span>
                          )}
                          {testMessage && configureKey === method.key && (
                            <span className="admin-payment-api-test-msg">{testMessage}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="admin-payment-config-grid">
                      {fields.map((field) => (
                        <div
                          key={field.name}
                          className={`owner-form-group${field.full ? ' admin-payment-config-full' : ''}`}
                        >
                          <label className="owner-form-label" htmlFor={`${method.key}-${field.name}`}>
                            {field.label}
                          </label>
                          <ConfigField
                            field={field}
                            methodKey={method.key}
                            value={config[field.name] || ''}
                            onChange={handleConfigChange}
                          />
                        </div>
                      ))}
                    </div>

                    {connectError && configureKey === method.key && (
                      <p className="admin-payment-api-error">{connectError}</p>
                    )}

                    <div className="admin-payment-config-actions">
                      {savedKey === method.key && (
                        <span className="admin-payment-saved-msg">Configuration saved to Supabase</span>
                      )}
                      {isApiMethod && (
                        <>
                          <button
                            type="button"
                            className="owner-btn owner-btn-primary"
                            onClick={() => handleConnectApi(method.key)}
                            disabled={connectingKey === method.key || saving}
                          >
                            {connectingKey === method.key ? 'Connecting...' : isConnected ? 'Reconnect via API' : 'Connect via API'}
                          </button>
                          <button
                            type="button"
                            className="owner-btn owner-btn-secondary"
                            onClick={() => handleTestApi(method.key)}
                            disabled={connectingKey === method.key || saving}
                          >
                            Test Connection
                          </button>
                          {isConnected && (
                            <button
                              type="button"
                              className="owner-btn owner-btn-secondary"
                              onClick={() => handleDisconnectApi(method.key)}
                              disabled={saving}
                            >
                              Disconnect
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        className="owner-btn owner-btn-secondary"
                        onClick={() => setConfigureKey(null)}
                      >
                        Cancel
                      </button>
                      {!isApiMethod && (
                        <button
                          type="button"
                          className="owner-btn owner-btn-primary"
                          onClick={() => handleSaveMethod(method.key)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className={`admin-payment-method-card${method.enabled ? ' enabled' : ''}`}>
                  <div className="admin-payment-method-main">
                    <div className="payment-method-icon">{method.icon}</div>
                    <div className="admin-payment-method-text">
                      <div className="payment-method-label">{method.label}</div>
                      <div className="payment-method-desc">{method.description}</div>
                      {isApiMethod && isConnected && (
                        <span className="admin-payment-api-connected-tag">API Connected</span>
                      )}
                    </div>
                    <span className={`owner-badge ${method.enabled ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                      {method.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="admin-payment-method-actions">
                    <ToggleSetting
                      checked={method.enabled}
                      onChange={(v) => toggleMethod(method.key, v)}
                      disabled={loading || saving}
                    />
                    <button
                      type="button"
                      className={`owner-btn owner-btn-sm ${isOpen ? 'owner-btn-primary' : 'owner-btn-secondary'}`}
                      onClick={() => handleConfigure(method.key)}
                    >
                      {isApiMethod ? 'Connect API' : 'Configure'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PaymentMethodsManagement;
